import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";

import { useTranslation } from "react-i18next";
import { maskString, maskFullNames } from "../../components/helpers/maskHelpers";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import StatusMessage from "../../components/ui/StatusMessage";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

export default function DriverForceRequestPayment() {
  const { t } = useTranslation();

  const [vendorWallets, setVendorWallets] = useState([]);
  const [forcePayPayers, setForcePayPayers] = useState([]);

  const [selectedPayerUserId, setSelectedPayerUserId] = useState("");
  const [selectedForceVendorWalletId, setSelectedForceVendorWalletId] = useState("");
  const [selectedForcePayerWalletId, setSelectedForcePayerWalletId] = useState("");

  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchVendorWallets();
    fetchForcePayPayers();
  }, []);

  const fetchVendorWallets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/driver/available-wallets`);
      setVendorWallets(res.data);
    } catch {
      console.error("❌ " + t("driver_force_request_payment.messages.fetch_vendor_wallets_failed"));
    }
  };

  const fetchForcePayPayers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/driver/forcepay-payers`);
      setForcePayPayers(res.data);
    } catch {
      console.error("❌ " + t("driver_force_request_payment.messages.fetch_payers_failed"));
    }
  };

  const passengerOptions = useMemo(() => {
    const uniquePassengers = new Map();

    forcePayPayers.forEach((p) => {

      const payerUserId = String(p.payer?.user_id);

      if (!uniquePassengers.has(payerUserId)) {
        uniquePassengers.set(payerUserId, {
          value: payerUserId,
          label: `${maskString(p.payer.userno)} | ${maskFullNames(
            p.payer.fullname
          )} | ${maskFullNames(p.payer.sponsor)}`,
        });
      }
    });

    return Array.from(uniquePassengers.values());
  }, [forcePayPayers]);

  const selectedPassengerOption = useMemo(() => {
    return (
      passengerOptions.find(
        (option) => option.value === String(selectedPayerUserId)
      ) || null
    );
  }, [passengerOptions, selectedPayerUserId]);

  const selectedPassengerWalletRows = useMemo(() => {
    if (!selectedPayerUserId) return [];

    return forcePayPayers.filter(
      (p) => String(p.payer?.user_id) === String(selectedPayerUserId)
    );
  }, [forcePayPayers, selectedPayerUserId]);



  const walletOptions = useMemo(() => {
  return selectedPassengerWalletRows.map((row) => {
    const vendorWallet = vendorWallets.find(
      (wallet) => Number(wallet.wallet_id) === Number(row.vendor_wallet_id)
    );

    return {
      vendor_wallet_id: String(row.vendor_wallet_id),
      payer_wallet_id: String(row.payer_wallet_id),
      label: [
        row.route_name || vendorWallet?.route_name,
        row.start_point && row.end_point
          ? `${row.start_point} → ${row.end_point}`
          : null,
        `${t("driver_force_request_payment.dropdown_item.ticket_balance_before")}: ${row.ticket_balance ?? 0}`,
        vendorWallet?.wallet_id
          ? `${t("driver_force_request_payment.dropdown_item.route_money_box_wallet")}: #${vendorWallet.wallet_id}`
          : null,
      ]
        .filter(Boolean)
        .join(" | "),
    };
  });
}, [selectedPassengerWalletRows, vendorWallets, t]);


  const handlePassengerChange = (option) => {
    const payerUserId = option ? option.value : "";

    setSelectedPayerUserId(payerUserId);
    setSelectedForceVendorWalletId("");
    setSelectedForcePayerWalletId("");
    setMessage("");

    if (!payerUserId) return;

    const matchingRows = forcePayPayers.filter(
      (p) => String(p.payer_user_id) === String(payerUserId)
    );

    if (matchingRows.length === 1) {
      setSelectedForceVendorWalletId(String(matchingRows[0].vendor_wallet_id));
      setSelectedForcePayerWalletId(String(matchingRows[0].payer_wallet_id));
    }
  };

  const handleWalletChange = (e) => {
    const vendorWalletId = e.target.value;

    setSelectedForceVendorWalletId(vendorWalletId);
    setSelectedForcePayerWalletId("");
    setMessage("");

    const selectedRow = selectedPassengerWalletRows.find(
      (p) => String(p.vendor_wallet_id) === String(vendorWalletId)
    );

    if (selectedRow) {
      setSelectedForcePayerWalletId(String(selectedRow.payer_wallet_id));
    }
  };

  const handleForcePay = async () => {
    if (!selectedForceVendorWalletId || !selectedForcePayerWalletId) {
      setMessage(t("driver_force_request_payment.messages.missing_wallets"));
      setMessageVariant("error");
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/driver/request-force-pay`, {
        from_wallet_id: Number(selectedForcePayerWalletId),
        delta_tickets_from_wallet: -1,
        delta_money_from_wallet: 0,
        to_wallet_id: Number(selectedForceVendorWalletId),
        delta_tickets_to_wallet: 1,
        delta_money_to_wallet: 0,
        header: "request-force-pay",
      });

      setMessage(t("driver_force_request_payment.messages.success"));
      setMessageVariant("success");

      setSelectedPayerUserId("");
      setSelectedForceVendorWalletId("");
      setSelectedForcePayerWalletId("");
    } catch {
      setMessage(t("driver_force_request_payment.messages.failure"));
      setMessageVariant("error");
    }
  };

  return (
    <StandardScreen
      topNav={<TopNav title={t("driver_force_request_payment.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("driver_force_request_payment.title")}>
              <Stack gap="3">

                {/* Passenger first */}
                <FormField label={t("driver_force_request_payment.fields.payer.label")}>
                  <Select
                    classNamePrefix="react-select"
                    options={passengerOptions}
                    value={selectedPassengerOption}
                    onChange={handlePassengerChange}
                    placeholder={t("driver_force_request_payment.fields.payer.placeholder")}
                    isClearable
                  />
                </FormField>

                {/* Wallet second */}
                <FormField label={t("driver_force_request_payment.fields.wallet.label")}>
                  <SelectInput
                    value={selectedForceVendorWalletId}
                    disabled={!selectedPayerUserId || walletOptions.length <= 0}
                    onChange={handleWalletChange}
                  >
                    <option value="">
                      {t("driver_force_request_payment.fields.wallet.placeholder")}
                    </option>

                    {walletOptions.map((wallet) => (
                      <option
                        key={`${wallet.vendor_wallet_id}-${wallet.payer_wallet_id}`}
                        value={wallet.vendor_wallet_id}
                      >
                        {wallet.label}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <Button
                  onClick={handleForcePay}
                  disabled={!selectedForceVendorWalletId || !selectedForcePayerWalletId}
                  fullWidth
                >
                  {t("driver_force_request_payment.actions.submit")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />

              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}